import {
  CreateExtractionUploadRequest,
  EXTRACTION_MAX_BYTES,
  ExtractionUploadResponse,
  StudyMaterialText,
} from '@quizdb/common';
import { MaterialPrepError } from './extraction/extractionTypes';

/**
 * What `uploadExtraction` needs to send `text` to the API's presigned URL. Its two members have
 * the same types as `PrepareMaterialDeps`'s, so a caller passes the same functions.
 */
export interface UploadExtractionDeps {
  createUpload: (body: CreateExtractionUploadRequest) => Promise<ExtractionUploadResponse>;
  /** Overrides the PUT's `fetch`, the way the tests stub it for S3. */
  put?: typeof fetch;
}

/**
 * Uploads an extraction's text under `id`, the second half of what `prepareMaterial` used to do
 * in one piece. A photo set has no lookup and always uploads this way at Start (PHO-D6); an
 * OCR'd PDF is cached under its own id instead. `signal` lets the caller cancel the work in
 * progress, as `prepareMaterial` does.
 */
export const uploadExtraction = async (
  id: string,
  text: StudyMaterialText,
  deps: UploadExtractionDeps,
  signal: AbortSignal
): Promise<void> => {
  const checkAborted = (): void => {
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
  };

  try {
    checkAborted();

    const body = JSON.stringify(text);
    const sizeBytes = new TextEncoder().encode(body).byteLength;
    // Compared against EXTRACTION_MAX_BYTES (PTX-D3), not reported as MaterialTooLarge: that
    // code's UI wording tells the user to use fewer pages, a limit on the job's material, which
    // does not apply here, before any job exists.
    if (sizeBytes > EXTRACTION_MAX_BYTES) {
      throw new MaterialPrepError('unknown');
    }

    const upload = await deps.createUpload({ id, sizeBytes });

    let response: Response;
    try {
      response = await (deps.put ?? fetch)(upload.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body,
        signal,
      });
    } catch (thrown) {
      if (thrown instanceof DOMException && thrown.name === 'AbortError') {
        throw thrown;
      }
      throw new MaterialPrepError('unknown');
    }
    if (!response.ok) {
      throw new MaterialPrepError('unknown');
    }
    checkAborted();
  } catch (thrown) {
    if (thrown instanceof MaterialPrepError) {
      throw thrown;
    }
    if (thrown instanceof DOMException && thrown.name === 'AbortError') {
      throw thrown;
    }
    throw new MaterialPrepError('unknown');
  }
};
